/**
 * OtpPanel — split out of AuthHub.jsx in 2026-02.
 */
import { useState, useEffect, useMemo } from "react";

import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "@phosphor-icons/react";
import { verifyOtp, resendOtp, setUserToken, formatUserApiError } from "../../lib/userApi";
import OtpInput from "../../components/auth/OtpInput";

import {
  GLASS, FieldLabel, TextInput, PasswordInput, DisplayField,
  GoldButton, GhostBackButton, useAuthShellBackground, parseCardDate,
} from "./_shared";

export default function OtpPanel({ pending, onVerified, onBack }) {
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cooldown, setCooldown] = useState(60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const masked = useMemo(() => {
    const [u, d] = pending.email.split("@");
    if (!u || !d) return pending.email;
    const head = u.slice(0, Math.min(2, u.length));
    const tail = u.length > 3 ? u.slice(-1) : "";
    return `${head}${"•".repeat(Math.max(2, u.length - head.length - tail.length))}${tail}@${d}`;
  }, [pending.email]);

  const submit = async (override) => {
    const c = (override || code).trim();
    if (c.length !== 6) {
      toast.error("الرجاء إدخال الرمز كاملاً (6 أرقام)");
      return;
    }
    setSubmitting(true);
    try {
      const res = await verifyOtp(pending.email, c);
      if (res.token) setUserToken(res.token);
      toast.success(pending.role === "client" ? "تم تفعيل حساب الزائر ✓" : "تم تفعيل حساب المحامي ✓");
      await onVerified();
    } catch (err) {
      toast.error(formatUserApiError(err));
      setCode("");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    try {
      await resendOtp(pending.email);
      toast.success("أرسلنا رمزاً جديداً إلى بريدك");
      setCooldown(60);
      setCode("");
    } catch (err) {
      toast.error(formatUserApiError(err));
    }
  };

  return (
    <>
      <GhostBackButton onClick={onBack} testid="otp-back-btn" />
      <section className={`${GLASS} p-5 sm:p-6`} data-testid="otp-panel">
        <h1 className="text-[22px] font-extrabold text-[#0A192F] leading-tight">تأكيد البريد</h1>
        <p className="text-[12.5px] text-[#5A6470] mt-1 mb-5">
          أرسلنا رمزاً من ستة أرقام إلى <span dir="ltr" className="font-bold text-[#0A192F]">{masked}</span>.
          صلاحيته 10 دقائق.
        </p>

        <OtpInput
          value={code}
          onChange={setCode}
          onComplete={(c) => submit(c)}
          disabled={submitting}
          testid="otp-input"
        />

        <div className="mt-5">
          <GoldButton onClick={() => submit()} disabled={submitting || code.length !== 6} testid="otp-verify-btn">
            {submitting ? "جارٍ التحقّق…" : "تفعيل الحساب"}
          </GoldButton>
        </div>

        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/60">
          <span className="text-[12px] text-[#5A6470]">لم يصِلك الرمز؟</span>
          <button
            type="button"
            data-testid="otp-resend-btn"
            onClick={handleResend}
            disabled={cooldown > 0}
            className="text-[12.5px] font-extrabold text-[#A88425] hover:underline disabled:no-underline disabled:text-[#8A8A8A] disabled:cursor-not-allowed"
          >
            {cooldown > 0 ? `إعادة الإرسال خلال ${cooldown} ث` : "إرسال رمز جديد"}
          </button>
        </div>
      </section>
    </>
  );
}

/* ─────────────────────── outer page ─────────────────────── */

